<template>
  <div class="room">
    <VideoRoomError />
    <VideoRoomLeft v-if="leftRoom" />
    <VideoRoomClosed v-else-if="closed" />
    <div v-else>
      <VideoRoomErrorModal v-if="networkError">
        {{ $t('errorText') }}
      </VideoRoomErrorModal>
      <VideoRoomErrorModal v-if="safariMicrophoneChange">
        {{ $t('safariChangeMic') }}
      </VideoRoomErrorModal>
      <div ref="fullScreen" class="video-room">
        <div class="video-room__wrap">
          <div class="video-room__left-wrap" :class="{full: isFullScreen}">
            <JanusRoomParticipants />
            <VideoRoomMainVideo />
            <JanusStreamControl />
          </div>
          <VideoRoomChat v-if="!isFullScreen" />
          <div v-if="showChat" class="overlay" />
          <button class="chat-btn" @click="showChat = true" />
        </div>
        <VideoRoomMediaErrors />
        <div>
          <MicrophoneChart />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  nextTick, onMounted, ref, watchEffect,
} from '@vue/composition-api';
import useStreams from '@/compositions/useStreams';
import useRoom from '@/compositions/useRoom';
import useJanusPublisher from '@/compositions/useJanusPublisher';
import VideoRoomMainVideo from '@/components/VideoRoomMainVideo.vue';
import JanusRoomParticipants from '@/components/VideoRoomParticipants.vue';
import JanusStreamControl from '@/components/VideoRoomControls.vue';
import MicrophoneChart from '@/components/MicrophoneChart.vue';
import VideoRoomError from '@/components/VideoRoomError.vue';
import VideoRoomClosed from '@/components/VideoRoomClosed.vue';
import VideoRoomLeft from '@/components/VideoRoomLeft.vue';
import VideoRoomMediaErrors from '@/components/VideoRoomMediaErrors.vue';
import VideoRoomChat from '@/components/VideoRoomChat.vue';
import useFullScreen from '@/compositions/useFullScreen';
import useChat from '@/compositions/useChat';
import useSocket from '@/compositions/useSocket';
import VideoRoomErrorModal from '@/components/VideoRoomErrorModal.vue';
import useErrors from '@/compositions/useErrors';
import useBrowser from '@/compositions/useBrowser';
import useMicrophone from '../compositions/useMicrophone';
import useTracks from '../compositions/useTracks';

export default {
  components: {
    VideoRoomErrorModal,
    VideoRoomChat,
    VideoRoomMediaErrors,
    VideoRoomClosed,
    VideoRoomLeft,
    VideoRoomError,
    MicrophoneChart,
    JanusStreamControl,
    JanusRoomParticipants,
    VideoRoomMainVideo,
  },
  setup(props, context) {
    const {
      closed,
      leftRoom,
    } = useRoom(context);
    const { setFullScreenElement, isFullScreen } = useFullScreen();
    const { showChat } = useChat();

    const { networkError, safariMicrophoneChange } = useErrors();
    const { sendEvent } = useSocket();

    const fullScreen = ref(null);

    watchEffect(() => {
      setFullScreenElement(fullScreen.value);
    });

    const { browser } = useBrowser();

    const {
      initCurrentStream, setOnEnd, setMicrophone, requestMediaStream, microphoneDisabled, lastMicrophoneDisabledState,
    } = useStreams();

    const {
      registerPublisherHandlers,
    } = useJanusPublisher();

    const {
      loadMicrophones,
      microphones,
    } = useMicrophone();

    const {
      replaceAndStopTrack,
    } = useTracks();

    onMounted(async () => {
      await registerPublisherHandlers();
      // setInterval(fetchPublishers, 5000);
      window.scrollTo(0, 1);
      await initCurrentStream();
    });

    navigator.mediaDevices.addEventListener('devicechange', async () => {
      await loadMicrophones();
      nextTick(async () => {
        const lastMicrophoneStateDisabled = microphoneDisabled.value;
        const micDefault = microphones.value[0];
        const microphoneFound = microphones.value.find(
          (microphone) => microphone.groupId === micDefault?.groupId && microphone.deviceId !== 'default',
        );
        setMicrophone(microphoneFound?.deviceId);

        if (browser.value.browser.name === 'Safari') {
          safariMicrophoneChange.value = true;
        }

        nextTick(async () => {
          const userMedia = await requestMediaStream({ video: false, audio: {} });
          const audioTrack = userMedia.getTracks().find((track) => track.kind === 'audio');
          setOnEnd(audioTrack);
          await replaceAndStopTrack(audioTrack);

          if (!lastMicrophoneDisabledState.value) {
            setTimeout(() => {
              microphoneDisabled.value = false;
              sendEvent('action:audio', { state: true });
              safariMicrophoneChange.value = false;
            }, 2000);
          }
        });
      });
    });

    return {
      closed,
      leftRoom,
      fullScreen,
      showChat,
      networkError,
      isFullScreen,
      safariMicrophoneChange,
    };
  },
};
</script>

<style lang="scss" scoped>
.fullscreen-exit {
  position: absolute;
  top: 50%;
}

.main-video {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  display: flex;
  align-items: center;
  align-content: center;
  background: #000;
}

.start-publish {
  align-self: center;
  width: 300px;
  position: absolute;
  background: #ccc;
  left: 50%;
  margin-left: -150px;
  padding: 1rem;
  z-index: 3;
}

.main-video video {
  margin: 0 auto;
  max-height: 100%;
  max-width: 100%;
  height: 100%;
}
.video-room-card {
  max-width: 37.5rem;
  padding: 1.875rem 3.125rem 2.625rem;
  background: #FFFFFF;
  box-shadow: 0 2px 0.5rem rgba(0, 0, 0, 0.15);
  border-radius: 0.5rem;
  text-align: left;
  margin: 1.875rem auto;
}
.video-room {
  background: #1C1C1E;
  font-size: 0.875rem;
  line-height: 1.43;
  color: #E0E0E0;
  &__wrap {
    display: flex;
    justify-content: space-between;
    height: 100vh;
  }
  &__left-wrap {
    width: calc(100% - 19.0625rem);
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100vh;
    overflow: hidden;
    &.full {
      width: 100%;
    }
  }
}
.chat-btn {
  display: none;
  width: 2.25rem;
  height: 2.25rem;
  background: rgba(0, 0, 0, 0.45) url("../assets/img/icons/chat.svg") no-repeat center;
  background-size: 1.125rem;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: none;
  border-radius: 50%;
  z-index: 3;
  position: absolute;
  top: 1rem;
  left: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
  &:focus, &:active, &:hover {
    outline: none;
    background-color: rgba(0, 0, 0, 0.65);
  }
}
@media (max-width: 1199px) {
  .video-room {
    &__left-wrap {
      width: 100%;
    }
  }
  .overlay {
    content: '';
    background: rgba(0, 0, 0, 0.6);
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 3;
  }
  .chat-btn {
    display: block;
  }
}
@media (max-width: 959px) {
  .chat-btn {
    top: 0.75rem;
    left: 0.75rem;
  }
}
@media (max-width: 599px) {
  .video-room-card {
    padding: 1.875rem 1.875rem 2.5rem;
  }
  .chat-btn {
    top: 0.75rem;
    left: 0.75rem;
  }
}
</style>

<i18n locale="en" lang="yaml">
errorText: >-
  There were connection troubles. Trying to reconnect...
  Check your internet connection.
safariChangeMic: >-
  You have connected new device. Setup started...
</i18n>

<i18n locale="ru" lang="yaml">
errorText: >-
  Возникли трудности с подключением. Производится попытка переподключения...
  Проверьте ваше соединение с интернетом.
safariChangeMic: >-
  Вы подключили новое устройство. Происходит настройка...
</i18n>
